import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../../../components-v2/Theme/theme';
import { shadowSm } from '../../../../../styles/shadows';
import { CssSelectors } from '../../../../../styles/styles';

// the Files tab renders in a panel much narrower than the viewport, so the stacked
// layout keys off the wrapper's own width (container query), not a media query
const STACKED_QUERY = `@container (max-width: ${THEME.breakpoints.md}px)`;

export const styleSheet = StyleSheet.create({
	container: {
		height: '100%',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: THEME.spacing.lg,
		boxSizing: 'border-box',
	},
	actions: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: SPACE[12],
		marginBottom: SPACE[16],
	},
	portalBanner: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[8],
		marginBottom: SPACE[16],
		padding: `${SPACE[12]}px ${SPACE[16]}px`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.info.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	filterTrigger: {
		minWidth: 200,
	},
	requestList: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[12],
		padding: `${SPACE[16]}px ${SPACE[24]}px`,
	},
	requestCard: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		overflow: 'hidden',
	},
	requestCardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[16],
		padding: `${SPACE[16]}px ${SPACE[24]}px`,
	},
	requestCardIcon: {
		width: 40,
		height: 40,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
		color: THEME.colors.icon.gray,
	},
	requestCardSummary: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
	requestCardSummaryInline: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'baseline',
		columnGap: `${SPACE[16]}px`,
	},
	requestCardTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[8],
		minWidth: 0,
	},
	requestCardTitle: {
		minWidth: 0,
		margin: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
	requestCardMeta: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	requestCardMuted: {
		fontStyle: 'italic',
	},
	requestCardFilesTrigger: {
		width: '100%',
		height: 'auto',
		justifyContent: 'flex-start',
		gap: SPACE[8],
		padding: `${SPACE[12]}px ${SPACE[24]}px`,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 0,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
	},
	requestCardFiles: {
		padding: `${SPACE[12]}px ${SPACE[24]}px ${SPACE[16]}px`,
	},
	sectionCard: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		overflow: 'hidden',
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		gap: SPACE[16],
		padding: `${SPACE[16]}px ${SPACE[24]}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	sectionHeading: {
		display: 'flex',
		alignItems: 'baseline',
		gap: SPACE[8],
	},
	sectionTitle: {
		margin: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	sectionCount: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		color: THEME.colors.text.light,
	},
	sectionHint: {
		width: 24,
		height: 24,
		padding: 0,
		alignSelf: 'center',
	},
	sectionHintTooltip: {
		maxWidth: 280,
	},
	sectionActions: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: SPACE[16],
	},
	sectionFooter: {
		display: 'flex',
		justifyContent: 'center',
		padding: SPACE[8],
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	filterRow: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[8],
	},
	emptyMessage: {
		padding: SPACE[16],
		textAlign: 'center',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	pageTitle: {
		display: 'flex',
		gap: THEME.spacing.md,
		alignItems: 'center',
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	iconContainer: {
		height: 48,
		width: 48,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: '#DBEAFE',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[24],
	},
	dataGridWrapper: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflowX: 'auto',
		containerType: 'inline-size',
	},
	dataGridWrapperFlush: {
		border: 'none',
		borderRadius: 0,
	},
	dataGrid: {
		borderBottom: 'none',
	},
	dataGridHeader: {
		[STACKED_QUERY]: {
			display: 'none',
		},
	},
	dataGridRow: {
		[STACKED_QUERY]: {
			display: 'grid',
			gridTemplateColumns: '1fr auto',
			alignItems: 'start',
			columnGap: THEME.spacing.md,
			padding: THEME.spacing.md,
			borderTop: THEME.common.border,
			':first-child': {
				borderTop: 'none',
			},
			// the base DataGrid row pads first/last cells via a higher-specificity child
			// selector; re-declare it here so the card's own padding is the only gutter
			...CssSelectors.allChildren({ paddingLeft: 0 }, ':first-child'),
			...CssSelectors.allChildren({ paddingRight: 0 }, ':last-child'),
		},
	},
	dataGridCell: {
		[STACKED_QUERY]: {
			border: 'none',
			padding: 0,
		},
	},
	// A zero max-width lets this auto-layout column shrink below its content and take the leftover width,
	// so long names truncate instead of widening the table past its card.
	cellFilename: {
		width: '100%',
		maxWidth: 0,
		[STACKED_QUERY]: {
			width: 'auto',
			maxWidth: 'none',
			minWidth: 0,
			overflowWrap: 'anywhere',
		},
	},
	cellNoWrap: {
		whiteSpace: 'nowrap',
	},
	cellDelete: {
		[STACKED_QUERY]: {
			width: 'auto',
			gridColumn: 2,
		},
	},
	cellHiddenWhenStacked: {
		[STACKED_QUERY]: {
			display: 'none',
		},
	},
	filenameCell: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		[STACKED_QUERY]: {
			alignItems: 'flex-start',
			gap: THEME.spacing.md,
		},
	},
	fileDetails: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: THEME.spacing.xs,
	},
	filenameLink: {
		height: 'auto',
		minWidth: 0,
		maxWidth: '100%',
		padding: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		justifyContent: 'flex-start',
		textAlign: 'left',
	},
	filenameText: {
		minWidth: 0,
		...THEME.common.truncate,
		[STACKED_QUERY]: {
			whiteSpace: 'normal',
			overflow: 'visible',
		},
	},
	fileMeta: {
		display: 'none',
		[STACKED_QUERY]: {
			display: 'flex',
			flexDirection: 'column',
			gap: THEME.spacing.xs,
		},
	},
	fileMetaLine: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	sourceBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: `2px ${THEME.spacing.sm}px`,
		border: THEME.common.border,
		borderRadius: 6,
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	fileIcon: {
		flexShrink: 0,
	},
	actionsCell: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	actionButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: THEME.spacing.xs,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	deleteColumn: {
		width: 40,
		paddingRight: 0,
		flexShrink: 0,
	},
	hiddenInput: {
		display: 'none',
	},
	fileList: {
		margin: 0,
		paddingLeft: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	dropzone: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		border: `2px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.xl,
		cursor: 'pointer',
		transition: 'all 0.2s',
		marginTop: THEME.spacing.md,
		':hover': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.primary.light,
		},
	},
	dropzoneActive: {
		borderColor: THEME.colors.primary.main,
		backgroundColor: THEME.colors.primary.light,
	},
	dropzoneContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		textAlign: 'center',
		pointerEvents: 'none',
	},
	dropzoneTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	dropzoneDescription: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	uploadedFilesList: {
		marginTop: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	filesCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	fileRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		maxHeight: 200,
		overflowY: 'auto',
	},
	fileRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
	},
	fileRowName: {
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
});
